import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from 'use-debounce';

import {
  auroMetalSaurus,
  backgroundGrey5,
  borderGray,
  positiveGreen,
  squidInk,
  warmGray,
  white,
} from 'lib/css/colors';
import { BRANDS_FOR_DROPDOWN } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import VerificationStatusBadge from 'components/shared/VerificationStatusBadge';

const RESULT_LIMIT = 50;

function BrandSearchPanel({ onPick, excludedBrandIds = [] }) {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery] = useDebounce(searchInput, 300);

  const { data, loading } = useGet(BRANDS_FOR_DROPDOWN, {
    search: searchQuery,
    limit: RESULT_LIMIT,
  });

  const excludedSet = useMemo(
    () => new Set(excludedBrandIds.map(id => Number(id))),
    [excludedBrandIds],
  );

  const visibleBrands = useMemo(
    () => (data?.data ?? []).filter(brand => !excludedSet.has(Number(brand.id))),
    [data, excludedSet],
  );

  return (
    <div className={css(styles.panel)}>
      <div className={css(styles.searchInputWrapper)}>
        <FontAwesomeIcon icon={faSearch} className={css(styles.searchIcon)} />
        <Form.Control
          type="text"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search brand..."
          className={css(styles.searchInput)}
        />
      </div>
      <div className={css(styles.resultsList)}>
        {loading ? (
          <div className={css(styles.statusRow)}>
            <Spinner animation="border" size="sm" className="mr-2" />
            <span>Loading brands</span>
          </div>
        ) : visibleBrands.length === 0 ? (
          searchQuery.trim().length < 2 ? (
            <div className={css(styles.statusRow, styles.emptyText)}>
              Type at least 2 characters to search
            </div>
          ) : (
            <div className={css(styles.statusRow, styles.emptyText)}>No matching brands</div>
          )
        ) : (
          visibleBrands.map(brand => (
            <div key={brand.id} className={css(styles.row)}>
              <div className={css(styles.rowText)}>
                <span className={css(styles.rowName)} title={brand.name}>
                  {brand.name}
                </span>
                <span className={css(styles.rowMfr)} title={brand.global_manufacturer_name || ''}>
                  {brand.global_manufacturer_name
                    ? `by ${brand.global_manufacturer_name}`
                    : 'No manufacturer'}
                </span>
              </div>
              <div className={css(styles.rowMeta)}>
                {brand.verification_status && (
                  <VerificationStatusBadge status={brand.verification_status} />
                )}
                <Button
                  className={css(styles.addButton)}
                  onClick={() => onPick(Number(brand.id))}
                  title="Add as duplicate"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  panel: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  searchInputWrapper: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    top: '50%',
    left: 10,
    transform: 'translateY(-50%)',
    color: auroMetalSaurus,
    fontSize: '0.85rem',
    pointerEvents: 'none',
  },
  searchInput: {
    border: `1px solid ${borderGray}`,
    borderRadius: 6,
    fontSize: '0.9rem',
    padding: '8px 12px 8px 32px',
  },
  resultsList: {
    border: `1px solid ${borderGray}`,
    borderRadius: 6,
    height: 180,
    overflowY: 'auto',
    backgroundColor: white,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 8px',
    color: warmGray,
    fontSize: '0.85rem',
  },
  emptyText: {
    fontStyle: 'italic',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 10px',
    borderBottom: `1px solid ${backgroundGrey5}`,
    ':last-child': {
      borderBottom: 'none',
    },
    ':hover': {
      backgroundColor: backgroundGrey5,
    },
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  rowName: {
    fontWeight: 600,
    color: squidInk,
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowMfr: {
    fontSize: '0.78rem',
    color: warmGray,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  addButton: {
    width: 28,
    height: 28,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: positiveGreen,
    border: 'none',
    color: white,
    fontSize: '0.85rem',
    ':hover': {
      backgroundColor: positiveGreen,
      opacity: 0.9,
    },
  },
});

BrandSearchPanel.propTypes = {
  onPick: PropTypes.func.isRequired,
  excludedBrandIds: PropTypes.array,
};

export default BrandSearchPanel;
